import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/http/client';

import type { QuoConnectResponse, QuoConnectVars, QuoStatus } from './types';

export const quoStatusQueryKey = (orgId: number | string) =>
  ['quoStatus', orgId] as const;

export function useQuoStatus(orgId: number | string) {
  return useQuery<QuoStatus>({
    queryKey: quoStatusQueryKey(orgId),
    queryFn: () => apiGet<QuoStatus>(`/api/orgs/${orgId}/quo/status/`),
    // Don't fire against a placeholder org id (0) while org state is still
    // loading — it would produce an avoidable failed /api/orgs/0/quo/status/ call.
    enabled: Boolean(orgId),
  });
}

export function useConnectQuo(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation<QuoConnectResponse, unknown, QuoConnectVars>({
    mutationFn: ({ apiKey, autoCreate, backfill }) =>
      apiPost<QuoConnectResponse>(`/api/orgs/${orgId}/quo/connect/`, {
        api_key: apiKey,
        auto_create_unknown_contacts: autoCreate,
        backfill,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: quoStatusQueryKey(orgId) }),
  });
}

export function useUpdateQuoSettings(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation<unknown, unknown, boolean>({
    mutationFn: (autoCreate: boolean) =>
      apiPatch<unknown>(`/api/orgs/${orgId}/quo/settings/`, {
        auto_create_unknown_contacts: autoCreate,
      }),
    // PATCH response shape is unspecified by the contract → refetch authoritative status.
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: quoStatusQueryKey(orgId) }),
  });
}

export function useDisconnectQuo(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation<unknown, unknown>({
    mutationFn: () => apiDelete<unknown>(`/api/orgs/${orgId}/quo/connection/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: quoStatusQueryKey(orgId) }),
  });
}
